import React, { FC } from "react";
import { darken, Link } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { useHistory } from "react-router-dom";
import OverflowTooltip from "../../../../components/OverflowTooltip/OverflowTooltip";
import Button from "../../../../components/Button";
import FontIcon from "../../../../components/FontIcon";
import { useAuth } from "../../../auth/hooks/useAuth";
import { UserRole } from "../../../user/enums/UserRole";
import { Routes } from "../../../../constants/routes";
import { urlReplacer } from "../../../../utils";
import { useWizard } from "../../../wizard/context";
import useServicesQueries from "../../../services/hooks/useServicesQueries";
import ChooseStudentForm from "../../../services/modals/chooseStudentForMessage/Content";
import { validator } from "../../../services/modals/chooseStudentForMessage/validator";
import theme from "../../../../themes";

const useStyles = makeStyles(() => ({
  header: {
    padding: "24px 32px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minHeight: 130,
  },
  teacherInfoMainContainer: {
    display: "flex",
  },
  teacherImageContainer: {
    margin: "auto 0px",
  },
  teacherImage: {
    backgroundSize: "100% auto",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    borderRadius: "50%",
    objectFit: "cover",
  },
  teacherInfoContainer: {
    marginLeft: 32,
    maxWidth: 714,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  teacherName: {
    fontSize: 25,
    fontWeight: "bold",
    color: "#231F20",
    marginTop: 7,
    lineHeight: 1.1,
  },
  sendMessageButton: {
    height: "28px",
    width: "189px",
    padding: "8px 16px !important",
    marginTop: 12,
  },
  messageIcon: {
    fontSize: 14,
    marginRight: 8,
  },
  hyperlink: {
    fontWeight: "bold",
    color: theme.palette.info.main,
    "&:hover": {
      color: darken(theme.palette.info.main, 0.2),
      cursor: "pointer",
    },
    textDecoration: "none",
  },
}));

interface TeacherInfoWidgetProps {
  teacherId: string;
  teacherName: string;
  profilePicture: string;
}

export const TeacherInfoWidget: FC<TeacherInfoWidgetProps> = ({
  teacherId,
  teacherName,
  profilePicture,
}) => {
  const {
    user,
    data: { isAuthenticated, authorizing },
  } = useAuth();
  const classes = useStyles();
  const history = useHistory();
  const { openWizard } = useWizard();
  const { getStudents } = useServicesQueries();

  const isTeacher = user?.role === UserRole.Teacher;

  const sendMessage = async () => {
    if (user?.role === UserRole.Student) {
      history.push(
        urlReplacer(`/0/${Routes.USER_MESSAGES}`, {
          userRole: UserRole.Teacher,
          recipientId: teacherId,
        }),
      );
    } else if ([UserRole.Agent, UserRole.Parent].includes(user?.role)) {
      const { data: students } = await getStudents(user.id);
      const studentId = await openWizard({
        steps: [
          {
            width: "500px",
            titleConfig: {
              titleText: "Choose a Student",
            },
            content: ChooseStudentForm,
            validator: validator,
            stepName: "chooseStudent",
          },
        ],
        submitButtonText: "Go to Messages",
        getInitialData: async () => ({ injectedData: { students } }),
        saveCallback: async (data) => ({
          data: data?.chooseStudent?.student?.id,
        }),
        showSuccessNotification: false,
      });

      if (!studentId) return;

      history.push(
        urlReplacer(
          `/0/${Routes.STUDENT}/${Routes.MESSAGES}?userRole=${UserRole.Teacher}&recipientId=${teacherId}`,
          {
            id: studentId,
          },
        ),
      );
    } else if (!!user) {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content:
              "Apologies, but this functionality isn't accessible yet for users with your account type. If you require further assistance, please get in touch with our support team.",
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
    } else {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content: () => (
              <p>
                Sorry, this feature is only available to logged-in users. Please{" "}
                <Link className={classes.hyperlink} href={`/${Routes.LOGIN}`}>
                  log in
                </Link>{" "}
                to write a message.
              </p>
            ),
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
    }
  };

  return (
    <div className={classes.header}>
      <div className={classes.teacherInfoMainContainer}>
        <div className={classes.teacherImageContainer}>
          <img
            alt={"Teacher Profile"}
            src={profilePicture}
            className={classes.teacherImage}
            width={74}
            height={74}
          />
        </div>
        <div className={classes.teacherInfoContainer}>
          <div className={classes.teacherName}>
            <OverflowTooltip text={teacherName} />
          </div>
          {!isTeacher && (
            <Button
              className={classes.sendMessageButton}
              type={"submit"}
              variant={"contained"}
              color={"secondary"}
              fullWidth
              onClick={sendMessage}
            >
              <FontIcon className={classes.messageIcon} name={"message"} />
              Send a message
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
